import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Modal, Popconfirm, Tooltip } from "antd";
import { useUploadNotification } from "../../context/UploadNotificationContext";
import {
  ensureAiClaimFileFolder,
  pollAiZipJob,
  summarizeAiZipJob,
  uploadZipServerSide,
  zipJobTrayFields,
} from "../../service/aiZipUploadService";
import {
  ACTION_LABEL,
  dismissMatter,
  fetchPullQueue,
  flagMatter,
  parkMatter,
  queueIsMoving,
  resolveFlag,
  retryMatter,
  unparkMatter,
} from "../../service/efolderPullQueue";
import {
  Actions,
  AttentionBadge,
  Cell,
  Count,
  Empty,
  ErrorBanner,
  FlagChip,
  HiddenInput,
  Muted,
  Name,
  Page,
  Problem,
  Progress,
  Row,
  Rows,
  Section,
  SectionHeader,
  Subtitle,
  Title,
  TitleRow,
} from "./EfolderPullsStyled";

/**
 * Claim file pulls — the VBMS downloaders' work list (Matthew, 2026-10-31).
 *
 * Three sections, straight from `GET /efolder-pulls/queue/`:
 *   Needs a pull  newly opened VA matters with no claim file yet (never the
 *                 legacy book: the backend's cutoff is the go-live day).
 *                 The name opens the file; "Upload zip" takes the eFolder
 *                 Express download right here, pinned to that matter.
 *   Parked        hidden for a week ("VA hasn't processed our 21-22 yet").
 *   Pulled        progress in plain words, problems in red with the one
 *                 button that fixes each (upload again / retry / flag).
 *
 * The zip goes through the same server-side path as the matter page's AI
 * Upload (`aiZipUploadService`) and shows in the global upload tray, so a
 * downloader can leave the page while it finishes.
 */

const REFRESH_MS = 15000;
const PARK_DEFAULT_NOTE = "VA hasn't processed our 21-22 yet";

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const fmtWhen = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return fmtDate(value);
};

const describeError = (error) =>
  error?.response?.data?.error || error?.response?.data?.detail || error?.message || "Something went wrong.";

const EfolderPullsPage = () => {
  const navigate = useNavigate();
  const { addUpload, updateUpload, addUploadAbortController, addFailedUpload } = useUploadNotification();

  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({}); // matterId -> action in flight
  const [showParked, setShowParked] = useState(false);
  const [flagTarget, setFlagTarget] = useState(null); // row being flagged
  const [flagNote, setFlagNote] = useState("");
  const [localUploads, setLocalUploads] = useState({}); // matterId -> percent

  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const next = await fetchPullQueue();
      setQueue(next);
      setError("");
    } catch (err) {
      setError(describeError(err));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Keep refreshing while anything is moving (or a local upload is running).
  const moving = useMemo(() => queueIsMoving(queue) || Object.keys(localUploads).length > 0, [queue, localUploads]);
  useEffect(() => {
    if (!moving) return undefined;
    const timer = setInterval(() => load({ quiet: true }), REFRESH_MS);
    return () => clearInterval(timer);
  }, [moving, load]);

  const run = async (matterId, actionName, fn) => {
    setBusy((b) => ({ ...b, [matterId]: actionName }));
    try {
      const result = await fn();
      await load({ quiet: true });
      return result;
    } catch (err) {
      setError(describeError(err));
      return null;
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[matterId];
        return next;
      });
    }
  };

  // ---- the zip itself -----------------------------------------------------

  const pickZipFor = (row) => {
    uploadTargetRef.current = row;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const uploadZip = async (zipFile, row) => {
    const matterId = row.matter_id;
    const uploadId = `ai-zip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const abortController = new AbortController();
    setLocalUploads((u) => ({ ...u, [matterId]: 0 }));
    addUpload({
      id: uploadId,
      fileName: zipFile.name,
      matchName: zipFile.name,
      fileSize: zipFile.size,
      progress: 0,
      status: "uploading",
      type: "ai-zip",
      matterId,
      matterLabel: row.veteran_name || undefined,
    });
    addUploadAbortController(uploadId, abortController);
    try {
      try {
        await ensureAiClaimFileFolder(matterId);
      } catch (_) {
        // The backend files into AI Claim File regardless; this only
        // pre-creates the folder so the tree looks right sooner.
      }
      const job = await uploadZipServerSide(zipFile, {
        matterId,
        folderId: null,
        signal: abortController.signal,
        onProgress: (percent) => {
          updateUpload(uploadId, { progress: percent, status: "uploading" });
          setLocalUploads((u) => ({ ...u, [matterId]: percent }));
        },
      });
      updateUpload(uploadId, { ...zipJobTrayFields(job), progress: 100, status: "uploading", serverJobId: job.job_id });
      setLocalUploads((u) => {
        const next = { ...u };
        delete next[matterId];
        return next;
      });
      await load({ quiet: true });
      const finalJob = await pollAiZipJob(job.job_id, {
        onUpdate: (j) => updateUpload(uploadId, zipJobTrayFields(j)),
      });
      if (finalJob.status === "failed" || finalJob.status === "aborted") {
        updateUpload(uploadId, { status: "failed", error: summarizeAiZipJob(finalJob) });
      } else {
        updateUpload(uploadId, { ...zipJobTrayFields(finalJob), progress: 100, status: "completed" });
      }
      await load({ quiet: true });
    } catch (err) {
      setLocalUploads((u) => {
        const next = { ...u };
        delete next[matterId];
        return next;
      });
      if (abortController.signal.aborted) return;
      addFailedUpload?.({
        id: uploadId,
        fileName: zipFile.name,
        fileSize: zipFile.size,
        type: "ai-zip",
        status: "failed",
        matterId,
        matterLabel: row.veteran_name || undefined,
        error: describeError(err),
      });
      setError(`"${zipFile.name}": ${describeError(err)}`);
      await load({ quiet: true });
    }
  };

  const onZipChosen = (event) => {
    const file = event.target.files && event.target.files[0];
    const row = uploadTargetRef.current;
    uploadTargetRef.current = null;
    if (!file || !row) return;
    if (!/\.zip$/i.test(file.name)) {
      setError("Pick the .zip file eFolder Express gave you.");
      return;
    }
    uploadZip(file, row);
  };

  // ---- problem buttons ----------------------------------------------------

  const openFlag = (row) => {
    setFlagTarget(row);
    setFlagNote("");
  };

  const submitFlag = async () => {
    const row = flagTarget;
    if (!row) return;
    setFlagTarget(null);
    await run(row.matter_id, "flag", () => flagMatter(row.matter_id, { note: flagNote, problems: row.problems }));
  };

  const act = (row, actionName) => {
    if (actionName === "upload_again") return pickZipFor(row);
    if (actionName === "retry") return run(row.matter_id, "retry", () => retryMatter(row.matter_id));
    if (actionName === "flag") return openFlag(row);
    if (actionName === "open_matter") return navigate(`/matter/${row.matter_id}`);
    return undefined;
  };

  const openMatter = (event, row) => {
    event.preventDefault();
    navigate(`/matter/${row.matter_id}`);
  };

  // ---- render -------------------------------------------------------------

  const counts = queue?.counts || {};
  const needsPull = queue?.needs_pull || [];
  const parked = queue?.parked || [];
  const pulled = queue?.pulled || [];

  const renderWho = (row) => (
    <Cell>
      <Name href={`/matter/${row.matter_id}`} onClick={(e) => openMatter(e, row)} data-testid="pull-row-name">
        {row.veteran_name || row.matter_id}
      </Name>
      {row.client_name ? <Muted>Client: {row.client_name}</Muted> : null}
      {row.flagged ? (
        <FlagChip title={row.flag_note || ""}>
          Flagged by {row.flagged_by || "someone"} {fmtWhen(row.flagged_at)}
        </FlagChip>
      ) : null}
    </Cell>
  );

  const renderOpened = (row) => (
    <Cell>
      <span>{fmtDate(row.opened_at)}</span>
      <Muted>{row.days_open === 0 ? "today" : `${row.days_open} day${row.days_open === 1 ? "" : "s"} open`}</Muted>
    </Cell>
  );

  const renderPeople = (row) => (
    <>
      <Cell>
        <Muted>Attorney</Muted>
        <span>{row.attorney || "—"}</span>
      </Cell>
      <Cell>
        <Muted>Case manager</Muted>
        <span>{row.case_manager || "—"}</span>
      </Cell>
    </>
  );

  return (
    <Page>
      <TitleRow>
        <Title>Claim file pulls</Title>
        {queue ? (
          <AttentionBadge $count={counts.attention || 0} data-testid="attention-badge">
            {counts.attention ? `${counts.attention} need${counts.attention === 1 ? "s" : ""} attention` : "Nothing needs attention"}
          </AttentionBadge>
        ) : null}
      </TitleRow>
      <Subtitle>
        VA matters opened since {queue?.since ? fmtDate(queue.since) : "go-live"} that still need their claim file pulled
        from eFolder Express. Click a name to open the file, or upload the zip right here.
      </Subtitle>

      {error ? <ErrorBanner role="alert">{error}</ErrorBanner> : null}
      {loading && !queue ? <Empty>Loading…</Empty> : null}

      {queue ? (
        <>
          <Section>
            <SectionHeader>
              <h2>Needs a pull</h2>
              <Count>{counts.needs_pull || 0}</Count>
            </SectionHeader>
            {needsPull.length === 0 ? (
              <Empty>Nothing waiting. New VA matters appear here as they are opened.</Empty>
            ) : (
              <Rows>
                {needsPull.map((row) => (
                  <Row key={row.matter_id} data-testid="needs-pull-row">
                    {renderWho(row)}
                    {renderOpened(row)}
                    {renderPeople(row)}
                    <Actions>
                      {localUploads[row.matter_id] !== undefined ? (
                        <Muted>Uploading… {localUploads[row.matter_id]}%</Muted>
                      ) : (
                        <Button type="primary" size="small" onClick={() => pickZipFor(row)}>
                          Upload zip
                        </Button>
                      )}
                      <Tooltip title={`${PARK_DEFAULT_NOTE}. Hides it for a week; it comes back on its own.`}>
                        <Button
                          size="small"
                          loading={busy[row.matter_id] === "park"}
                          onClick={() => run(row.matter_id, "park", () => parkMatter(row.matter_id, { note: PARK_DEFAULT_NOTE }))}
                        >
                          Park 1 week
                        </Button>
                      </Tooltip>
                      <Popconfirm
                        title="This matter never needs a claim file pulled?"
                        okText="Not needed"
                        onConfirm={() => run(row.matter_id, "dismiss", () => dismissMatter(row.matter_id, "not needed"))}
                      >
                        <Button size="small" type="text" danger>
                          Not needed
                        </Button>
                      </Popconfirm>
                    </Actions>
                  </Row>
                ))}
              </Rows>
            )}
          </Section>

          {parked.length > 0 ? (
            <Section>
              <SectionHeader>
                <h2>Parked</h2>
                <Count>{counts.parked || 0}</Count>
                <button type="button" onClick={() => setShowParked((v) => !v)}>
                  {showParked ? "hide" : "show"}
                </button>
              </SectionHeader>
              {showParked ? (
                <Rows>
                  {parked.map((row) => (
                    <Row key={row.matter_id} data-testid="parked-row">
                      {renderWho(row)}
                      {renderOpened(row)}
                      <Cell>
                        <Muted>Parked until</Muted>
                        <span>{fmtDate(row.parked_until)}</span>
                      </Cell>
                      <Cell>
                        <Muted>Why</Muted>
                        <span>{row.park_note || "—"}</span>
                      </Cell>
                      <Actions>
                        <Button
                          size="small"
                          loading={busy[row.matter_id] === "unpark"}
                          onClick={() => run(row.matter_id, "unpark", () => unparkMatter(row.matter_id))}
                        >
                          Bring back
                        </Button>
                      </Actions>
                    </Row>
                  ))}
                </Rows>
              ) : null}
            </Section>
          ) : null}

          <Section>
            <SectionHeader>
              <h2>Pulled</h2>
              <Count>{counts.pulled || 0}</Count>
            </SectionHeader>
            {pulled.length === 0 ? (
              <Empty>No pulls in progress. Finished pulls drop off after a day.</Empty>
            ) : (
              <Rows>
                {pulled.map((row) => (
                  <Row key={row.matter_id} $attention={row.attention} data-testid="pulled-row">
                    {renderWho(row)}
                    <Cell>
                      <Muted>Pulled {fmtWhen(row.job?.created_at)}</Muted>
                      <span>{row.job?.requested_by || ""}</span>
                    </Cell>
                    <Cell style={{ gridColumn: "span 2" }}>
                      <Progress $step={row.progress?.step} data-testid="pull-progress">
                        {row.progress?.text}
                      </Progress>
                      {(row.problems || []).map((p) => (
                        <Problem key={p.code} data-testid="pull-problem">
                          {p.text}
                          {p.detail && p.detail.length > 0 ? (
                            <ul>
                              {p.detail.map((name) => (
                                <li key={name}>{name}</li>
                              ))}
                            </ul>
                          ) : null}
                        </Problem>
                      ))}
                    </Cell>
                    <Actions>
                      {(row.problems || [])
                        .filter((p) => p.action)
                        .map((p) => (
                          <Button
                            key={p.code}
                            size="small"
                            type={p.action === "flag" ? "default" : "primary"}
                            danger={p.action === "upload_again"}
                            loading={busy[row.matter_id] === p.action}
                            onClick={() => act(row, p.action)}
                          >
                            {ACTION_LABEL[p.action] || p.action}
                          </Button>
                        ))}
                      {row.attention && !(row.problems || []).some((p) => p.action === "flag") ? (
                        <Button size="small" onClick={() => openFlag(row)}>
                          Flag for help
                        </Button>
                      ) : null}
                      {row.flagged ? (
                        <Button
                          size="small"
                          type="text"
                          loading={busy[row.matter_id] === "resolve-flag"}
                          onClick={() => run(row.matter_id, "resolve-flag", () => resolveFlag(row.matter_id))}
                        >
                          Resolve flag
                        </Button>
                      ) : null}
                    </Actions>
                  </Row>
                ))}
              </Rows>
            )}
          </Section>
        </>
      ) : null}

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        data-testid="pull-zip-input"
        onChange={onZipChosen}
      />

      <Modal
        title={flagTarget ? `Flag ${flagTarget.veteran_name || "this pull"} for help` : "Flag for help"}
        open={Boolean(flagTarget)}
        onOk={submitFlag}
        okText="Flag it"
        onCancel={() => setFlagTarget(null)}
      >
        <p style={{ marginTop: 0 }}>
          This puts the problem in front of Matthew and Brendan, with the file and what the queue shows attached.
        </p>
        <Input.TextArea
          rows={3}
          value={flagNote}
          onChange={(e) => setFlagNote(e.target.value)}
          placeholder="What you saw (optional)"
          data-testid="flag-note"
        />
      </Modal>
    </Page>
  );
};

export default EfolderPullsPage;
